import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";
import { TravelerEditMode, MINIMUM_DRIVER_AGE } from "@apac/travelers";
import { useI18nContext } from "@hopper-b2b/i18n";
import { nameRegex } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { ACTIVITIES_NAME_REGEX } from "@/utilities";

export interface UseTravellerDetailsFormFieldsProps {
  dateFormat?: string;
  showGender?: boolean;
  showNationality?: boolean;
  travelerEditMode?: TravelerEditMode;
}

export enum TravellerFormFields {
  FirstName = "firstName",
  MiddleName = "middleName",
  LastName = "lastName",
  DateOfBirth = "dateOfBirth",
  Gender = "gender",
  Nationality = "nationality",
}

export type TravellerDetailsFormContext = {
  [TravellerFormFields.FirstName]: string;
  [TravellerFormFields.MiddleName]: string;
  [TravellerFormFields.LastName]: string;
  [TravellerFormFields.DateOfBirth]: string;
  [TravellerFormFields.Gender]: string | undefined;
  [TravellerFormFields.Nationality]: string | undefined;
};

/**
 * RBC override of useTravellerDetailsFormFields that uses stricter validation
 * for activities (ACTIVITIES_NAME_REGEX) to comply with Viator's requirements.
 */
export const useTravellerDetailsFormFields = (
  props: UseTravellerDetailsFormFieldsProps = {}
) => {
  const {
    dateFormat = "DD/MM/YYYY",
    showGender = true,
    showNationality = false,
    travelerEditMode = TravelerEditMode.ALL,
  } = props;
  const { t, brand } = useI18nContext();

  // Use stricter regex for activities (Viator requirement)
  const isActivitiesMode =
    travelerEditMode === TravelerEditMode.ACTIVITIES_ONLY;
  const validationRegex = isActivitiesMode ? ACTIVITIES_NAME_REGEX : nameRegex;

  const travellerDetailsDefaultValues: TravellerDetailsFormContext = {
    [TravellerFormFields.FirstName]: "",
    [TravellerFormFields.MiddleName]: "",
    [TravellerFormFields.LastName]: "",
    [TravellerFormFields.DateOfBirth]: "",
    [TravellerFormFields.Gender]: undefined,
    [TravellerFormFields.Nationality]: brand.preferredCountryCode,
  };

  const commonNameValidationProperties = useMemo(
    () => ({
      pattern: {
        value: validationRegex,
        message: t("noSpecialCharactersError"),
      },
      maxLength: {
        value: 31,
        message: t("nameLengthExceedError"),
      },
    }),
    [t, validationRegex]
  );

  const formFields: Partial<Record<TravellerFormFields, FieldValues>> = {
    [TravellerFormFields.FirstName]: {
      required: t("checkoutSection.travelerForm.errors.firstNameRequired"),
      ...commonNameValidationProperties,
    },
    [TravellerFormFields.MiddleName]: {
      required: false,
      ...commonNameValidationProperties,
    },
    [TravellerFormFields.LastName]: {
      required: t("checkoutSection.travelerForm.errors.lastNameRequired"),
      ...commonNameValidationProperties,
    },
    [TravellerFormFields.DateOfBirth]: {
      required: t("checkoutSection.travelerForm.errors.dateOfBirthRequired"),
      validate: (birthdate: string) => {
        const dateDayjs = dayjs(birthdate, dateFormat, true);
        if (!dateDayjs.isValid() || dateDayjs.isAfter(dayjs())) {
          return t("enterAValidBirthDate");
        }
        if (
          travelerEditMode === TravelerEditMode.CARS_ONLY &&
          dayjs().diff(dateDayjs, "year") < MINIMUM_DRIVER_AGE
        ) {
          return t("enterAValidBirthDate");
        }
      },
    },
  };

  if (showGender) {
    formFields[TravellerFormFields.Gender] = {
      required: t("checkoutSection.travelerForm.errors.genderRequired"),
    };
  } else {
    delete travellerDetailsDefaultValues[TravellerFormFields.Gender];
  }

  if (showNationality) {
    formFields[TravellerFormFields.Nationality] = {
      required: t("travelerForm.errors.nationalityRequired"),
    };
  } else {
    delete travellerDetailsDefaultValues[TravellerFormFields.Nationality];
  }

  return { formFields, travellerDetailsDefaultValues };
};
